import React from "react";
import { useTranslation } from "react-i18next";
import { useAdminControls } from "../../../state/AdminControlsContext";
import { useAdminService } from "../../../data/adminService";
import { useAsyncData } from "../../../hooks/useAsyncData";
import { formatNumberDe } from "../../../utils/format";
import type { AdminSongSourceBreakdown } from "../../../graphql/types";
import "./ImportOriginCard.css";

/** The four origins in display order, each mapped to its CSS modifier. */
const SEGMENTS: { key: keyof AdminSongSourceBreakdown; modifier: string }[] = [
  { key: "chordProEditor", modifier: "chordpro" },
  { key: "openWith", modifier: "open-with" },
  { key: "onSongImport", modifier: "onsong" },
  { key: "songBookPlusImport", modifier: "songbook" },
];

/**
 * Import origins (FE-10, §6.7): where the songs created in the selected window
 * came from. Owns its own fetch so an error here leaves the rest of the
 * dashboard intact. Song counts and import runs are shown apart on purpose —
 * one import run produces many songs.
 */
export const ImportOriginCard: React.FC = () => {
  const { t } = useTranslation();
  const { rangeVars } = useAdminControls();
  const service = useAdminService();

  const { data, loading, error, reload } = useAsyncData(
    () => service.getImportOrigins(rangeVars),
    [service, rangeVars.range, rangeVars.from, rangeVars.to],
  );

  if (error) {
    return (
      <div className="lyra-error origin-error">
        <span>{t("dashboard.importOrigin.error")}</span>
        <button className="range-btn range-btn--ghost" onClick={reload}>
          {t("dashboard.retry")}
        </button>
      </div>
    );
  }

  const songs = data?.songs ?? null;
  const total = songs
    ? SEGMENTS.reduce((sum, segment) => sum + songs[segment.key], 0)
    : 0;

  return (
    <div className="origin-card">
      <div className="origin-head">
        <h2 className="origin-title">{t("dashboard.importOrigin.title")}</h2>
        <span className="origin-total">
          {t("dashboard.importOrigin.totalSongs", { value: formatNumberDe(total) })}
        </span>
      </div>

      {!songs && loading ? (
        <div className="lyra-skeleton origin-skeleton" />
      ) : total === 0 ? (
        <p className="origin-empty">{t("dashboard.importOrigin.empty")}</p>
      ) : (
        <>
          <div className="origin-track">
            {SEGMENTS.map((segment) => {
              const value = songs![segment.key];
              if (value === 0) return null;
              return (
                <div
                  key={segment.key}
                  className={`origin-seg origin-seg--${segment.modifier}`}
                  style={{ width: `${(value / total) * 100}%` }}
                />
              );
            })}
          </div>

          <div className="origin-legend">
            {SEGMENTS.map((segment) => (
              <span className="origin-legend-item" key={segment.key}>
                <span className={`origin-swatch origin-swatch--${segment.modifier}`} />
                {t(`dashboard.importOrigin.source.${segment.key}`)}
                <strong>{formatNumberDe(songs![segment.key])}</strong>
              </span>
            ))}
          </div>
        </>
      )}

      <div className="origin-runs">
        <span className="origin-runs-label">{t("dashboard.importOrigin.runsLabel")}</span>
        <span className="origin-runs-value">
          {t("dashboard.importOrigin.runsOnSong", {
            value: formatNumberDe(data?.imports.onSongBackup ?? 0),
          })}
          {" · "}
          {t("dashboard.importOrigin.runsSongBook", {
            value: formatNumberDe(data?.imports.songBookPlus ?? 0),
          })}
        </span>
      </div>

      <p className="origin-note">{t("dashboard.importOrigin.note")}</p>
    </div>
  );
};
